import { useState } from "react";
import DateRangeSelector from "../molecules/DateRangeSelector";
import { Box, Divider, Grid, Skeleton, Typography } from "@mui/material";
import { DashboardService } from "../../services/api";
import { DateRange } from "../../services/swagger";
import DashboardTicketCount from "../organisms/Dashboard/DashboardTicketCount";
import DashboardTicketResolution from "../organisms/Dashboard/DashboardTicketResolution";
import DashboardStatusOverview from "../organisms/Dashboard/DashboardStatusOverview";
import DashboardTicketStats from "../organisms/Dashboard/DashboardTicketStats";
import useBreakpoint from "../../hooks/useBreakpoint";
import DashboardTasks from "../organisms/Dashboard/DashboardTasks";
import useRefresh from "../../hooks/useRefresh";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

export default function DashboardPage() {
  const { t } = useTranslation("Dashboard");
  const [rangeDisplacement, setRangeDisplacement] = useState(0);
  const [range, setRange] = useState<DateRange>(DateRange.Month);
  const breakpoint = useBreakpoint();

  const loadDashboardData = async () => {
    const res = await DashboardService.getDashboard({
      dateRange: range,
      rangeDisplacement,
    });
    if (res?.data?.data) return res.data.data;
  };

  const { data, isLoading } = useQuery({
    queryFn: loadDashboardData,
    queryKey: ["dashboard", { range, rangeDisplacement }],
    refetchInterval: 1000 * 30,
  });

  const handleChangeRange = (range: DateRange, rangeDisplacement: number) => {
    setRange(range);
    setRangeDisplacement(rangeDisplacement);
  };

  useRefresh(loadDashboardData);

  return (
    <Box
      display="flex"
      flexDirection="column"
      height={1}
      p={1}
      sx={{
        overflow: "auto",
        WebkitUserSelect: "none",
        msUserSelect: "none",
        userSelect: "none",
      }}
    >
      <DateRangeSelector
        range={range}
        rangeDisplacement={rangeDisplacement}
        onChange={handleChangeRange}
      />
      <Box pt={2} />
      <Box
        flexGrow={breakpoint.md ? 1 : undefined}
        overflow={breakpoint.md ? "auto" : undefined}
      >
        {isLoading || !data ? (
          <Box width={1} overflow="hidden" position="relative">
            {!breakpoint.md && (
              <Box height={1} width={1} zIndex={100} position="absolute"></Box>
            )}
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, lg: 12, xl: 4 }}>
                <Skeleton width="100%" height={350} variant="rounded" />
              </Grid>
              <Grid size={{ xs: 12, lg: 6, xl: 4 }}>
                <Skeleton width="100%" height={350} variant="rounded" />
              </Grid>
              <Grid size={{ xs: 12, lg: 6, xl: 4 }}>
                <Skeleton width="100%" height={350} variant="rounded" />
              </Grid>
              <Grid size={12}>
                <Box>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Skeleton width="100%" height={100} variant="rounded" />
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Skeleton width="100%" height={100} variant="rounded" />
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Skeleton width="100%" height={100} variant="rounded" />
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Skeleton width="100%" height={100} variant="rounded" />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid size={12}>
                <Typography align="center" variant="h3">
                  {t("ActiveTasks")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Skeleton width="100%" height={70} variant="rounded" />
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box width={1} overflow="hidden" position="relative">
            {!breakpoint.md && (
              <Box height={1} width={1} zIndex={100} position="absolute"></Box>
            )}
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, lg: 12, xl: 4 }}>
                <DashboardTicketCount data={data} />
              </Grid>
              <Grid size={{ xs: 12, lg: 6, xl: 4 }}>
                <DashboardStatusOverview
                  data={data}
                  dateRange={range}
                  rangeDisplacement={rangeDisplacement}
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 6, xl: 4 }}>
                <DashboardTicketResolution data={data} />
              </Grid>
              <Grid size={12}>
                <DashboardTicketStats data={data} />
              </Grid>
              <Grid size={12}>
                <Typography align="center" variant="h3">
                  {t("ActiveTasks")}
                </Typography>
                <DashboardTasks data={data} />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
}
